import React, { useState } from 'react';

import IbIcon from '../../IbIcon';
import StatusModal from '../StatusModal';
import { IStatusInfoItem, StatusInfoState, StatusInfoTheme } from '../types';

import './index.less';
import {
  BAR_CLASS_NAME,
  DESCRIPTION_CLASS_NAME,
  ICON_CLASS_NAME,
  ITEM_CLASS_NAME,
  LINE_CLASS_NAME,
  MAIN_CLASS_NAME,
  MODAL_CLASS_NAME,
  SUBTITLE_CLASS_NAME,
  TITLE_CLASS_NAME,
} from './const';

export type StatusStepsStyle = 'compact' | 'wide';

export interface IStatusStepsProps {
  className?: string;
  rolling: boolean;
  direction?: 'horizontal' | 'vertical';
  style?: StatusStepsStyle;
  items: IStatusInfoItem[];
  current?: number;
  theme?: StatusInfoTheme;
  status?: StatusInfoState;
  onRestart?: () => void;
}

const StatusSteps: React.FC<IStatusStepsProps> = ({
  className,
  rolling,
  direction,
  style,
  items,
  current,
  theme,
  status,
  onRestart,
}) => {
  const [selectedItem, setSelectedItem] = useState<IStatusInfoItem>();

  const displaySubtitles = items.some((item) => item.subtitle);

  const onModalRestart = () => {
    if (rolling) {
      return;
    }

    setSelectedItem(undefined);
    onRestart?.();
  };

  const onModalClose = () => {
    setSelectedItem(undefined);
  };

  const renderBar = () => {
    return <div className={BAR_CLASS_NAME} />;
  };

  const renderIcon = (item: IStatusInfoItem) => {
    if (item.status === StatusInfoState.Process) {
      return <IbIcon spin className={ICON_CLASS_NAME} iconName="loading-four" size={16} />;
    }

    if (item.status === StatusInfoState.Finish) {
      return <IbIcon className={ICON_CLASS_NAME} iconName="check-one" size={16} />;
    }

    if (item.status === StatusInfoState.Error) {
      return <IbIcon className={ICON_CLASS_NAME} iconName="handle-x" size={16} />;
    }

    return <IbIcon className={ICON_CLASS_NAME} iconName="round" size={16} />;
  };

  const renderTitle = (item: IStatusInfoItem) => {
    const title = item.title || '';
    return (
      <div className={TITLE_CLASS_NAME} title={title}>
        {title}
      </div>
    );
  };

  const renderSubtitle = (item: IStatusInfoItem) => {
    const subtitle = item.subtitle || '';
    return (
      <div className={SUBTITLE_CLASS_NAME} title={subtitle}>
        {subtitle}
      </div>
    );
  };

  const renderDescription = (item: IStatusInfoItem) => {
    const description = item.description || '';
    return (
      <div className={DESCRIPTION_CLASS_NAME} title={description}>
        {description}
      </div>
    );
  };

  const renderHLine = (item: IStatusInfoItem) => {
    return (
      <div className={LINE_CLASS_NAME}>
        {renderBar()}
        {renderIcon(item)}
        {style === 'wide' && renderTitle(item)}
        {renderBar()}
      </div>
    );
  };

  const renderVLine = (item: IStatusInfoItem) => {
    return (
      <div className={LINE_CLASS_NAME}>
        {renderIcon(item)}
        {renderTitle(item)}
      </div>
    );
  };

  const renderHItem = (item: IStatusInfoItem, index: number) => {
    const onItemClick = () => {
      item.modal && setSelectedItem(item);
    };

    const itemClasses = [ITEM_CLASS_NAME, `${ITEM_CLASS_NAME}_${item.theme}`, `${ITEM_CLASS_NAME}_${item.status}`];
    if (item.modal) {
      itemClasses.push(MODAL_CLASS_NAME);
    }

    return (
      <div key={index} className={itemClasses.join(' ')} onClick={onItemClick}>
        {style === 'compact' && renderTitle(item)}
        {renderHLine(item)}
        {displaySubtitles && renderSubtitle(item)}
      </div>
    );
  };

  const renderVItem = (item: IStatusInfoItem, index: number) => {
    const onItemClick = () => {
      item.modal && setSelectedItem(item);
    };

    const itemClasses = [ITEM_CLASS_NAME, `${ITEM_CLASS_NAME}_${item.theme}`, `${ITEM_CLASS_NAME}_${item.status}`];
    if (item.modal) {
      itemClasses.push(MODAL_CLASS_NAME);
    }

    return (
      <div key={index} className={itemClasses.join(' ')} onClick={onItemClick}>
        {renderBar()}
        {renderVLine(item)}
        {renderDescription(item)}
        {renderBar()}
      </div>
    );
  };

  const renderItem = (item: IStatusInfoItem, index: number) => {
    return direction === 'vertical' ? renderVItem(item, index) : renderHItem(item, index);
  };

  const getTheme = (item: IStatusInfoItem): StatusInfoTheme => {
    if (item.theme) {
      return item.theme;
    }

    if (theme) {
      return theme;
    }

    return StatusInfoTheme.Green;
  };

  const getStatus = (item: IStatusInfoItem, index: number): StatusInfoState => {
    if (item.status) {
      return item.status;
    }

    if (current == undefined) {
      return StatusInfoState.Wait;
    }

    if (index < current) {
      return StatusInfoState.Finish;
    }

    if (index > current) {
      return StatusInfoState.Wait;
    }

    return status || StatusInfoState.Process;
  };

  const renderModal = () => {
    return (
      <StatusModal
        description={selectedItem?.description}
        problem={selectedItem?.problem}
        rolling={rolling}
        status={selectedItem?.status}
        subtitle={selectedItem?.subtitle}
        title={selectedItem?.title}
        visible={!!selectedItem}
        onClose={onModalClose}
        onRestart={onModalRestart}
      />
    );
  };

  items = items.map((item, index) => ({
    ...item,
    status: getStatus(item, index),
    theme: getTheme(item),
  }));

  const classes = [
    MAIN_CLASS_NAME,
    `${MAIN_CLASS_NAME}_${direction || 'horizontal'}`,
    `${MAIN_CLASS_NAME}_${style || 'compact'}`,
  ];
  if (className) {
    classes.push(className);
  }

  return (
    <div className={classes.join(' ')}>
      {items.map(renderItem)}
      {renderModal()}
    </div>
  );
};

export default StatusSteps;
